// Smart Sash Admin Panel - Certification Question Navigation Component
// Controls for navigating between questions and reordering them

import React from 'react';
import { Button } from '@/components/common';

export interface QuestionNavigationProps {
  currentIndex: number;
  totalQuestions: number;
  isEditMode: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onShiftLeft: () => void;
  onShiftRight: () => void;
  onAdd: () => void;
}

export const QuestionNavigation: React.FC<QuestionNavigationProps> = ({
  currentIndex,
  totalQuestions,
  isEditMode,
  onPrevious,
  onNext,
  onShiftLeft,
  onShiftRight,
  onAdd,
}) => {
  const isFirstQuestion = currentIndex <= 0;
  const isLastQuestion = currentIndex >= totalQuestions - 1;

  return (
    <nav
      className="d-flex justify-content-between align-items-center certification-nav-controls"
      aria-label="Question navigation"
    >
      {/* Navigation buttons: Previous / Next */}
      <div className="d-flex gap-2" role="group" aria-label="Navigate between questions">
        <Button
          variant="secondary"
          outline
          icon="fas fa-chevron-left"
          onClick={onPrevious}
          disabled={isFirstQuestion}
          title="Previous question (Left Arrow)"
          aria-label={`Go to previous question${isFirstQuestion ? ' (disabled - at first question)' : ''}`}
          className="certification-nav-btn"
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          outline
          icon="fas fa-chevron-right"
          onClick={onNext}
          disabled={isLastQuestion}
          title="Next question (Right Arrow)"
          aria-label={`Go to next question${isLastQuestion ? ' (disabled - at last question)' : ''}`}
          className="certification-nav-btn"
        >
          Next
        </Button>
      </div>

      {/* Question counter */}
      <div className="text-center" aria-live="polite" aria-atomic="true">
        <span className="text-muted">
          Question {currentIndex + 1} of {totalQuestions}
        </span>
      </div>

      {/* Edit mode buttons: Shift Left / Shift Right / Add */}
      {isEditMode ? (
        <div className="d-flex gap-2" role="group" aria-label="Reorder and add questions">
          <Button
            variant="primary"
            outline
            icon="fas fa-arrow-left"
            onClick={onShiftLeft}
            disabled={isFirstQuestion}
            title="Move question earlier in order"
            aria-label={`Move question earlier${isFirstQuestion ? ' (disabled - already first)' : ''}`}
          />
          <Button
            variant="primary"
            outline
            icon="fas fa-arrow-right"
            onClick={onShiftRight}
            disabled={isLastQuestion}
            title="Move question later in order"
            aria-label={`Move question later${isLastQuestion ? ' (disabled - already last)' : ''}`}
          />
          <Button
            variant="primary"
            icon="fas fa-plus"
            onClick={onAdd}
            title="Add a new question"
            aria-label="Add new question"
          >
            Add Question
          </Button>
        </div>
      ) : (
        // Empty div to maintain flex spacing in view mode
        <div aria-hidden="true"></div>
      )}
    </nav>
  );
};

export default QuestionNavigation;
